import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import ShareIcon from "@mui/icons-material/Share";
import { Container, Fade, IconButton, Stack, Typography } from "@mui/material";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { EventDetails } from "../types";
import {
  DesktopHeroSection,
  HeroContent,
  HeroImage,
  HeroOverlay,
  InfoChip,
  MobileHeaderOverlay,
  MobileHeroSection
} from "../styles";

interface HeroSectionProps {
  eventDetails: EventDetails;
  isMobile: boolean;
}

export function HeroSection({ eventDetails, isMobile }: HeroSectionProps) {
  const navigate = useNavigate();

  return (
    <>
      {/* Mobile Hero Section */}
      <MobileHeroSection>
        {eventDetails.photo && (
          <>
            <HeroImage
              src={process.env.REACT_APP_BACKEND_URL + eventDetails.photo}
              alt={eventDetails.name}
            />
            <MobileHeaderOverlay>
              <IconButton
                onClick={() => navigate(-1)}
                sx={{ color: 'white', backgroundColor: 'rgba(0,0,0,0.3)' }}
              >
                <ArrowBackIcon />
              </IconButton>
              <IconButton
                sx={{ color: 'white', backgroundColor: 'rgba(0,0,0,0.3)' }}
              >
                <ShareIcon />
              </IconButton>
            </MobileHeaderOverlay>
          </>
        )}
      </MobileHeroSection>

      {/* Desktop Hero Section */}
      {!isMobile && (
        <Container maxWidth="xl" sx={{ px: { xs: 2, md: 3 } }}>
          <Fade in timeout={800}>
            <DesktopHeroSection>
              {eventDetails.photo && (
                <>
                  <HeroImage
                    src={process.env.REACT_APP_BACKEND_URL + eventDetails.photo}
                    alt={eventDetails.name}
                    onLoad={(e) => {
                      const img = e.target as HTMLImageElement;
                      const container = img.parentElement;
                      if (container) {
                        container.style.height = `${img.offsetHeight}px`;
                      }
                    }}
                  />
                  <HeroOverlay />
                  <HeroContent>
                    <Typography 
                      variant="h2" 
                      component="h1" 
                      fontWeight="bold" 
                      sx={{ 
                        fontSize: { md: '2.5rem', lg: '3rem', xl: '3.5rem' },
                        lineHeight: 1.2,
                        mb: 2,
                        textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                      }}
                    >
                      {eventDetails.name}
                    </Typography>
                    
                    <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 3 }}>
                      <InfoChip 
                        className="location"
                        icon={<LocationOnIcon />} 
                        label={eventDetails.location} 
                      />
                      <InfoChip 
                        className="date"
                        icon={<CalendarTodayIcon />} 
                        label={dayjs(eventDetails.start_date).format("dddd, MMMM DD, YYYY")} 
                      />
                      <InfoChip 
                        className="time"
                        icon={<AccessTimeIcon />} 
                        label={`${dayjs(eventDetails.start_date).format("h:mm A")} - ${dayjs(eventDetails.end_date).format("h:mm A")}`} 
                      />
                    </Stack>

                    <Typography 
                      variant="h6" 
                      sx={{ 
                        opacity: 0.95,
                        lineHeight: 1.6,
                        maxWidth: '500px',
                        fontSize: { md: '1.125rem', lg: '1.25rem' },
                        textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
                      }}
                    >
                      {eventDetails.description}
                    </Typography>
                  </HeroContent>
                </>
              )}
            </DesktopHeroSection>
          </Fade>
        </Container>
      )}
    </>
  );
}
